import React, { useEffect } from 'react';
import { 
  GlobeAltIcon, 
  ServerIcon, 
  CloudIcon, 
  BeakerIcon,
  DevicePhoneMobileIcon,
  CpuChipIcon 
} from '@heroicons/react/24/outline';
import HeroBackground from '../components/HeroBackground';
import '../styles/hero.css';
import SEO from '../components/SEO';

interface ServiceItem {
  icon: JSX.Element;
  title: string;
  description: string;
}

interface TechnologyItem {
  icon: string;
  name: string;
}

const Home: React.FC = () => {
  useEffect(() => {
    // Check if there's a stored scroll target
    const scrollTarget = localStorage.getItem('scrollTarget');
    if (scrollTarget) {
      // Clear the stored target
      localStorage.removeItem('scrollTarget');
      // Wait for the page to render
      setTimeout(() => {
        const element = document.getElementById(scrollTarget);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, []);


  return (
    <div>
      <SEO 
        title="David Sheeshka | Web Development & Digital Services"
        description="Professional web development, cloud solutions, and AI integrations. Specializing in React, TypeScript, and cutting-edge digital solutions to elevate your business."
        keywords="web development, cloud computing, AI solutions, React, TypeScript, digital transformation, software consulting"
        image="/images/hero-background.jpg"
      />
       {/* Hero Section */}
       <section 
        id="home" 
        className="relative h-screen flex items-center justify-center hero-section"
      >
        {/* Unsplash Hero Background */}
        <HeroBackground query="code development workspace" />
        
        <div className="relative z-10 text-center max-w-4xl mx-auto px-4">
          <h1 className="text-5xl font-bold mb-4">
            Turning Ideas Into
            <br />
            <span className="text-blue-500">Digital Reality</span>
          </h1>
          <p className="text-lg text-gray-300 mb-8">
            Professional web development and digital services to help your business
            thrive in the digital age.
          </p>
          <div className="flex gap-4 justify-center">
            <a href="#contact" className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg transition-colors">
              Get Started
            </a>
            <a href="#services" className="border border-white hover:bg-white/10 text-white px-6 py-3 rounded-lg transition-colors">
              Learn More
            </a>
          </div>
        </div>
      </section>
      
            {/* About Section */}
            <section id="about" className="py-20 bg-[#0B0F1A]">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8 text-center">About Me</h2>
          <p className="text-gray-400 max-w-3xl mx-auto text-center leading-relaxed">
            With years of experience in software development and digital marketing, I specialize in creating compelling websites that drive results. 
            My approach combines technical expertise with creative problem-solving to deliver solutions that exceed client expectations.
          </p>
          <p className="text-gray-400 max-w-3xl mx-auto text-center mt-4 leading-relaxed">
            I believe in staying ahead of industry trends, and continuously developing my skills. By providing cutting-edge digital solutions for my clients, 
            we build your brand's ongoing legacy in a competitive web application. I'm here to help bring your vision to life.
          </p>
        </div>
      </section>

            {/* Services Section */}
            <section id="services" className="py-20 bg-[#0B0F1A]">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service: ServiceItem, index: number) => (
              <div key={index} className="bg-[#151B2C] p-6 rounded-lg">
                <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4">
                  {service.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2">{service.title}</h3>
                <p className="text-gray-400">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

            {/* Technologies Section */}
            <section id="technologies" className="py-20 bg-[#0B0F1A]">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Technologies I Work With</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-blue-500 mb-4">Frontend</h3>
              <div className="grid grid-cols-2 gap-4">
                {frontendTech.map((tech, index) => (
                  <div key={index} className="bg-[#151B2C] p-4 rounded-lg flex flex-col items-center">
                    <img src={tech.icon} alt={tech.name} className="w-8 h-8 mb-2" />
                    <span className="text-sm">{tech.name}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-blue-500 mb-4">Backend</h3>
              <div className="grid grid-cols-2 gap-4">
                {backendTech.map((tech, index) => (
                  <div key={index} className="bg-[#151B2C] p-4 rounded-lg flex flex-col items-center">
                    <img src={tech.icon} alt={tech.name} className="w-8 h-8 mb-2" />
                    <span className="text-sm">{tech.name}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-blue-500 mb-4">Cloud & DevOps</h3>
              <div className="grid grid-cols-2 gap-4">
                {cloudTech.map((tech, index) => (
                  <div key={index} className="bg-[#151B2C] p-4 rounded-lg flex flex-col items-center">
                    <img src={tech.icon} alt={tech.name} className="w-8 h-8 mb-2" />
                    <span className="text-sm">{tech.name}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-blue-500 mb-4">Tools & Others</h3>
              <div className="grid grid-cols-2 gap-4">
                {toolsTech.map((tech, index) => (
                  <div key={index} className="bg-[#151B2C] p-4 rounded-lg flex flex-col items-center">
                    <img src={tech.icon} alt={tech.name} className="w-8 h-8 mb-2" />
                    <span className="text-sm">{tech.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-[#0B0F1A]">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Get In Touch</h2>
          <p className="text-gray-300 text-lg">
            Feel free to reach out at{' '}
            <a
              href="mailto:hello@davidsheeshka.com"
              className="text-blue-400 hover:text-blue-300 transition-colors"
            >
              hello@davidsheeshka.com
            </a>
          </p>
        </div>
      </section>
    </div>
  );
};


const services: ServiceItem[] = [
  {
    icon: <GlobeAltIcon className="w-6 h-6 text-blue-500" />,
    title: 'Web Development',
    description: 'Quality websites that look great and work even better. Built with modern tech and best practices.'
  },
  {
    icon: <ServerIcon className="w-6 h-6 text-blue-500" />,
    title: 'Database',
    description: 'Set up and optimize your database needs. Get the right data structure for your app.'
  },
  {
    icon: <CloudIcon className="w-6 h-6 text-blue-500" />,
    title: 'Cloud Services',
    description: 'Secure cloud hosting and deployment. Scale your application efficiently and securely.'
  },
  {
    icon: <BeakerIcon className="w-6 h-6 text-blue-500" />,
    title: 'AI & Machine Learning',
    description: 'Custom AI models, automation, and integration. Transform data into smart solutions.'
  },
  {
    icon: <DevicePhoneMobileIcon className="w-6 h-6 text-blue-500" />,
    title: 'Mobile App Development',
    description: 'Native and cross-platform mobile applications for iOS and Android devices.'
  },
  {
    icon: <CpuChipIcon className="w-6 h-6 text-blue-500" />,
    title: 'Digital Marketing',
    description: 'SEO, digital marketing, and analytics. Help your business grow and reach new audiences.'
  }
];

const frontendTech: TechnologyItem[] = [
  { icon: '/icons/react.svg', name: 'React' },
  { icon: '/icons/next.svg', name: 'Next.js' },
  { icon: '/icons/vue.svg', name: 'Vue' },
  { icon: '/icons/angular.svg', name: 'Angular' }
];

const backendTech: TechnologyItem[] = [
  { icon: '/icons/node.svg', name: 'Node.js' },
  { icon: '/icons/python.svg', name: 'Python' },
  { icon: '/icons/mongodb.svg', name: 'MongoDB' },
  { icon: '/icons/postgresql.svg', name: 'PostgreSQL' }
];

const cloudTech: TechnologyItem[] = [
  { icon: '/icons/aws.svg', name: 'AWS' },
  { icon: '/icons/docker.svg', name: 'Docker' },
  { icon: '/icons/kubernetes.svg', name: 'Kubernetes' },
  { icon: '/icons/terraform.svg', name: 'Terraform' }
];

const toolsTech: TechnologyItem[] = [
  { icon: '/icons/git.svg', name: 'Git' },
  { icon: '/icons/vscode.svg', name: 'VS Code' },
  { icon: '/icons/figma.svg', name: 'Figma' },
  { icon: '/icons/cypress.svg', name: 'Cypress' }
];

export default Home;